import React, { useState, useCallback, forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { Box, Grid, IconButton } from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { rfidTagService } from '@/services';
import type { RfidTagFilter } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';
import NewWcnTable, {
    DataTableColumn,
    DataTablePagination,
    DataTableSorting
} from '@/components/datatable/NewWcnTable.tsx';
import { DeleteConfirmationDialog, WcnTextField } from '@/components';

interface RfidTagFiltersTableProps {
    onEdit: (item: RfidTagFilter) => void;
    onAdd: (e?: React.MouseEvent) => void;
}

export interface RfidTagFiltersTableRef {
    load: () => void;
}

const RfidTagFiltersTable = forwardRef<RfidTagFiltersTableRef, RfidTagFiltersTableProps>(({ onEdit, onAdd }, ref) => {
    const { t } = useTranslation();
    const { notifySuccess, notifyErrorResponse } = useNotifications();
    const [filters, setFilters] = useState<RfidTagFilter[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [query, setQuery] = useState('');
    const [sortBy, setSortBy] = useState<string>('filter');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [loading, setLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<RfidTagFilter | null>(null);

    const load = useCallback(async () => {
        setLoading(true);

        try {
            const payload = {
                page: page + 1,
                itemsPerPage: rowsPerPage,
                sortBy: sortBy ? [sortBy] : [],
                sortDesc: sortBy ? [sortDir === 'desc'] : [],
                query: query || ''
            };
            const response = await rfidTagService.loadRfidTagFilters(payload);

            setFilters(response.items);
            setTotal(response.total);
        } catch (error) {
            console.error('Failed to fetch filters:', error);
            setFilters([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, query, sortBy, sortDir]);

    useEffect(() => {
        // Initial load
        load();

        const unsubscribeReload = eventBus.on(EventTypes.WIDGETS_RELOAD, load);

        return () => {
            unsubscribeReload();
        };
    }, [load]);

    useImperativeHandle(ref, () => ({
        load
    }));

    const handleClearQuery = useCallback(() => {
        setQuery('');
        setPage(0);
    }, []);

    const handleDeleteClick = useCallback((item: RfidTagFilter) => {
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!itemToDelete) return;
        setLoading(true);
        try {
            await rfidTagService.deleteRfidTagFilter(itemToDelete.id);

            notifySuccess();
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            load();
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    }, [itemToDelete, notifySuccess, notifyErrorResponse, load]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
    }, []);

    // DataTable columns definition
    const columns: DataTableColumn<RfidTagFilter>[] = useMemo(
        () => [
            {
                text: t('filter'),
                value: 'filter',
                width: 200
            },
            {
                text: t('actions'),
                value: 'actions',
                width: 100,
                isAction: true,
                sortable: false,
                render: (row) => (
                    <>
                        <IconButton type="button" size="small" onClick={() => onEdit(row)} title={t('edit')}>
                            <i className="bi bi-pencil" />
                        </IconButton>
                        <IconButton
                            type="button"
                            size="small"
                            onClick={() => handleDeleteClick(row)}
                            title={t('delete')}
                        >
                            <i className="bi bi-trash3" />
                        </IconButton>
                    </>
                ),
                align: 'left'
            }
        ],
        []
    );

    const sorting: DataTableSorting = {
        defaultSortBy: 'filter',
        sortBy,
        sortDirection: sortDir,
        onSortChange: (field, dir) => {
            setSortBy(field);
            setSortDir(dir);
            setPage(0);
        }
    };

    // DataTable pagination config
    const pagination: DataTablePagination = {
        count: total,
        page,
        rowsPerPage,
        onPageChange: (_event, newPage) => setPage(newPage),
        onRowsPerPageChange: (e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
        },
        rowsPerPageOptions: [5, 10, 15]
    };

    return (
        <Box>
            <Box>
                <Grid container xs={12} spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={11}>
                        <WcnTextField
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setPage(0);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    load();
                                }
                            }}
                            label={t('search')}
                            size="small"
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {query && (
                                            <IconButton size="small" onClick={handleClearQuery}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        <IconButton
                                            size="small"
                                            onClick={() => load()}
                                            color="inherit"
                                            title={t('search')}
                                        >
                                            <SearchIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={1} sx={{ mt: 2.5 }}>
                        <IconButton type="button" onClick={(e) => onAdd(e)} color="inherit" title={t('add')}>
                            <AddIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </Box>
            <NewWcnTable<RfidTagFilter>
                data={filters}
                loading={loading}
                columns={columns}
                pagination={pagination}
                sorting={sorting}
                noDataMessage={t('noData')}
                size="small"
            />
            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                heading={t('delete') + '?'}
                message={t('deleteItemMessage')}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />
        </Box>
    );
});

RfidTagFiltersTable.displayName = 'RfidTagFiltersTable';

export default RfidTagFiltersTable;
